import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Alert, AlertTitle, Box, Slide, Stack } from '@mui/material';

export type ToastLevel = 'info' | 'success' | 'warning' | 'error';

interface Toast {
  id: number;
  level: ToastLevel;
  title?: string;
  message: string;
}

interface ToastApi {
  show: (message: string, opts?: { level?: ToastLevel; title?: string }) => void;
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
  warning: (message: string, title?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** How long each toast stays on screen before auto-dismiss. */
const AUTO_HIDE_MS = 5_000;
/** Cap concurrent toasts so a burst can't bury the screen. */
const MAX_VISIBLE = 4;

/**
 * App-wide toast/snackbar host. Renders a stacked column of dismissible alerts at
 * the top-right and exposes an imperative API via {@link useToast}. No extra deps —
 * built on MUI's Alert + Slide.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    const tm = timers.current.get(id);
    if (tm) { clearTimeout(tm); timers.current.delete(id); }
  }, []);

  const show = useCallback<ToastApi['show']>((message, opts) => {
    const id = ++seq.current;
    setToasts((list) => [...list, { id, message, level: opts?.level ?? 'info', title: opts?.title }].slice(-MAX_VISIBLE));
    timers.current.set(id, setTimeout(() => dismiss(id), AUTO_HIDE_MS));
  }, [dismiss]);

  // Clear any pending timers on unmount.
  useEffect(() => () => { timers.current.forEach((t) => clearTimeout(t)); timers.current.clear(); }, []);

  const api = useMemo<ToastApi>(() => ({
    show,
    success: (m, title) => show(m, { level: 'success', title }),
    error: (m, title) => show(m, { level: 'error', title }),
    info: (m, title) => show(m, { level: 'info', title }),
    warning: (m, title) => show(m, { level: 'warning', title }),
  }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Box
        sx={{
          position: 'fixed', top: { xs: 70, sm: 80 }, right: 16, zIndex: (t) => t.zIndex.snackbar,
          pointerEvents: 'none', width: 'min(380px, calc(100vw - 32px))',
        }}
      >
        <Stack spacing={1.25}>
          {toasts.map((t) => (
            <Slide key={t.id} direction="left" in mountOnEnter unmountOnExit>
              <Alert
                severity={t.level}
                variant="filled"
                onClose={() => dismiss(t.id)}
                sx={{ width: '100%', boxShadow: 6, pointerEvents: 'auto' }}
              >
                {t.title && <AlertTitle sx={{ mb: 0.25 }}>{t.title}</AlertTitle>}
                {t.message}
              </Alert>
            </Slide>
          ))}
        </Stack>
      </Box>
    </ToastContext.Provider>
  );
}

/** Imperative toast API. Must be used under a {@link ToastProvider}. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
